export const RESOURCES_ROUTE = '/resources';
export const PROVIDER_CATEGORY_BASE_ROUTE = '/resources/providers';
export const HUMAN_REVIEW_CATEGORY_SLUG = 'human-review';
export const AUTHOR_MARKETING_CATEGORY_SLUG = 'author-marketing';
export const PUBLISHING_SUPPORT_CATEGORY_SLUG = 'publishing-support';
export const HUMAN_REVIEW_PROVIDERS_ROUTE = `${PROVIDER_CATEGORY_BASE_ROUTE}/${HUMAN_REVIEW_CATEGORY_SLUG}`;
export const AUTHOR_MARKETING_PROVIDERS_ROUTE = `${PROVIDER_CATEGORY_BASE_ROUTE}/${AUTHOR_MARKETING_CATEGORY_SLUG}`;
export const PUBLISHING_SUPPORT_PROVIDERS_ROUTE = `${PROVIDER_CATEGORY_BASE_ROUTE}/${PUBLISHING_SUPPORT_CATEGORY_SLUG}`;
export const LEGACY_HUMAN_REVIEW_PROVIDERS_ROUTE = '/human-review-providers';

export type ProviderBadge =
  | 'independent-provider'
  | 'scribeshadow-workflows'
  | 'scribeshadow-user-discount'
  | 'referral-participant'
  | 'education-contributor';

export type ProviderBadgeDefinition = {
  label: string;
  description: string;
};

export type ServiceProviderCategory = {
  id: string;
  title: string;
  routeSlug: string;
  description: string;
  introCopy?: string[];
  metaTitle: string;
  metaDescription: string;
  disclaimer: string;
};

export type ServiceProvider = {
  id: string;
  categoryIds: string[];
  name: string;
  subtitle: string;
  description: string;
  categoryContent?: Record<string, {
    subtitle?: string;
    description?: string;
    services?: string[];
  }>;
  logoSrc?: string;
  logoAlt?: string;
  logoTheme?: 'dark' | 'light' | 'warm';
  logoFit?: 'contain' | 'cover';
  logoInset?: 'none' | 'comfortable';
  badges: ProviderBadge[];
  services: string[];
  discount?: string;
  contactName?: string;
  contactEmail?: string;
  phone?: string;
  websiteUrl?: string;
  disclaimer: string;
};

export const providerBadgeDefinitions: Record<ProviderBadge, ProviderBadgeDefinition> = {
  'independent-provider': {
    label: 'Independent Provider',
    description:
      'This provider is an independent third-party business that offers services relevant to authors using ScribeShadow.',
  },
  'scribeshadow-workflows': {
    label: 'Familiar with ScribeShadow Workflows',
    description:
      'This provider has experience working with ScribeShadow-related translation workflows, files, or authors.',
  },
  'scribeshadow-user-discount': {
    label: 'Offers ScribeShadow User Discount',
    description:
      'This provider offers a documented discount, special rate, or coupon for ScribeShadow users or ScribeShadow-referred authors.',
  },
  'referral-participant': {
    label: 'Referral Participant',
    description:
      'This provider participates in referral tracking and may compensate ScribeShadow for completed, paid projects that originate from ScribeShadow referrals.',
  },
  'education-contributor': {
    label: 'Education Contributor',
    description:
      'This provider may contribute educational resources, webinars, tutorials, Q&A sessions, or other author-facing materials related to human review, editing, translation review, or responsible publishing workflows.',
  },
};

export const serviceProviderCategories: ServiceProviderCategory[] = [
  {
    id: 'human-review',
    title: 'Human Review Provider Directory',
    routeSlug: HUMAN_REVIEW_CATEGORY_SLUG,
    description:
      'Some authors choose to add an independent human review step after using ScribeShadow.',
    introCopy: [
      'Some authors choose to add an independent human review step after using ScribeShadow.',
      'The providers listed below are independent third-party businesses that offer proofreading, editing, foreign-language review, or translation review services. Some providers may be familiar with ScribeShadow workflows, offer discounts to ScribeShadow users, participate in referral tracking, or contribute educational resources for authors.',
      'ScribeShadow does not control these providers\' pricing, availability, turnaround times, service quality, customer communications, refunds, or dispute resolution. Inclusion in this directory does not create an exclusive partnership, official provider relationship, or guarantee of service quality.',
      'Authors should contact providers directly to discuss pricing, timelines, language coverage, and service details.',
    ],
    metaTitle: 'Human Review Provider Directory | ScribeShadow',
    metaDescription:
      'Explore independent third-party human review, proofreading, editing, and translation review providers familiar with AI-assisted translation workflows.',
    disclaimer:
      'ScribeShadow does not control these providers\' pricing, availability, turnaround times, service quality, customer communications, refunds, or dispute resolution. Inclusion in this directory does not create an exclusive partnership, official provider relationship, or guarantee of service quality.',
  },
  {
    id: 'author-marketing',
    title: 'Author Marketing & Social Media Provider Directory',
    routeSlug: AUTHOR_MARKETING_CATEGORY_SLUG,
    description:
      'Authors publishing translated editions may want help with audience-building, social media, and storefront setup.',
    introCopy: [
      'Authors publishing translated editions may want help with audience-building, social media, and storefront setup.',
      'The providers listed below are independent third-party businesses that offer author marketing, social media management, platform setup, or related publishing support services.',
      'ScribeShadow does not control these providers\' pricing, availability, turnaround times, service quality, customer communications, refunds, or dispute resolution. Inclusion in this directory does not create an exclusive partnership, official provider relationship, or guarantee of service quality.',
      'Authors should contact providers directly to discuss scope, language coverage, timelines, and service details.',
    ],
    metaTitle: 'Author Marketing & Social Media Provider Directory | ScribeShadow',
    metaDescription:
      'Explore independent third-party author marketing, social media management, and publishing support providers for translated editions.',
    disclaimer:
      'ScribeShadow does not control these providers\' pricing, availability, turnaround times, service quality, customer communications, refunds, or dispute resolution. Inclusion in this directory does not create an exclusive partnership, official provider relationship, or guarantee of service quality.',
  },
  {
    id: 'publishing-support',
    title: 'Publishing Support & Formatting Provider Directory',
    routeSlug: PUBLISHING_SUPPORT_CATEGORY_SLUG,
    description:
      'Authors may need help preparing translated editions for ebook, print, audiobook, direct-store, or retailer workflows.',
    introCopy: [
      'Authors may need help preparing translated editions for ebook, print, audiobook, direct-store, or retailer workflows.',
      'The providers listed below are independent third-party businesses that offer formatting, publishing assistant services, freelancer coordination, direct-store setup, audiobook production coordination, or related publishing support.',
      'ScribeShadow does not control these providers\' pricing, availability, turnaround times, service quality, customer communications, refunds, or dispute resolution. Inclusion in this directory does not create an exclusive partnership, official provider relationship, or guarantee of service quality.',
      'Authors should contact providers directly to discuss scope, pricing, timelines, language coverage, and service details.',
    ],
    metaTitle: 'Publishing Support & Formatting Provider Directory | ScribeShadow',
    metaDescription:
      'Explore independent third-party ebook formatting, print formatting, author PA, and publishing support providers for translated editions.',
    disclaimer:
      'ScribeShadow does not control these providers\' pricing, availability, turnaround times, service quality, customer communications, refunds, or dispute resolution. Inclusion in this directory does not create an exclusive partnership, official provider relationship, or guarantee of service quality.',
  },
];

export const getProvidersForCategory = (
  category: ServiceProviderCategory,
  providers: ServiceProvider[],
): ServiceProvider[] => providers.filter((provider) => provider.categoryIds.includes(category.id));

export const getActiveProviderCategories = (
  providers: ServiceProvider[],
): ServiceProviderCategory[] =>
  serviceProviderCategories.filter((category) => getProvidersForCategory(category, providers).length > 0);

export const getProviderCategoryBySlug = (slug?: string): ServiceProviderCategory | undefined =>
  serviceProviderCategories.find((category) => category.routeSlug === slug);

export const getProviderCategoryRoute = (category: ServiceProviderCategory): string =>
  `${PROVIDER_CATEGORY_BASE_ROUTE}/${category.routeSlug}`;
